import { createSlice } from '@reduxjs/toolkit';
import { NotificationFlagsState } from './notification-flags.state';
import { RefreshNotificationFlags } from './refresh-notification-flags';
import { NotificationFlag } from './notification-flag';
import { RefreshNotificationFlagsExceptPrefix } from './refresh-notification-flags-except-prefix';

const initialState: NotificationFlagsState = {
    flags: [],
    flagsToDelete: [],
    deletedFlags: [],
    refreshAfterRemoval: false,
    isLoading: false,
    status: 'succeeded',
    error: '',
};

const notificationFlagsSlice = createSlice({
    name: 'notificationFlags',
    initialState,
    reducers: {
        addNotificationFlagToDelete(state, action) {
            const isAddedToDeleteList = state.flagsToDelete.some(
                (f) =>
                    f.eventCode.toLowerCase() ===
                    action.payload.eventCode.toLowerCase()
            );
            const isAddedToDeletedList = state.deletedFlags.some(
                (f) =>
                    f.eventCode.toLowerCase() ===
                    action.payload.eventCode.toLowerCase()
            );
            if (!isAddedToDeleteList && !isAddedToDeletedList) {
                state.flagsToDelete.push(action.payload);
                state.refreshAfterRemoval = action.payload.refreshAfterRemoval;
                state.flags = state.flags.filter(
                    (f) =>
                        f.eventCode.toLowerCase() !==
                        action.payload.eventCode.toLowerCase()
                );
            }
        },
        deleteNotificationFlags(state, action) {
            const deletedFlags = action.payload as NotificationFlag[];
            state.flagsToDelete = state.flagsToDelete.filter((f) =>
                deletedFlags.every(
                    (d) =>
                        d.eventCode.toLowerCase() !== f.eventCode.toLowerCase()
                )
            );
            state.deletedFlags.push(...deletedFlags);
        },
        removeNotificationFlagLocally(state, action) {
            const eventCode = action.payload as string;
            state.flags = state.flags.filter(
                (f) => f.eventCode.toLowerCase() !== eventCode.toLowerCase()
            );
        },
        updateNotificationFlagCountByCode(state, action) {
            const updatedFlag = action.payload as NotificationFlag;

            if (updatedFlag) {
                const flagToUpdate = state.flags.find((flag) =>
                    flag.eventCode
                        .toLocaleLowerCase()
                        .includes(updatedFlag.eventCode.toLocaleLowerCase())
                );
                const flagIndex = state.flags.indexOf(flagToUpdate);

                state.flags[flagIndex] = {
                    ...flagToUpdate,
                    count: updatedFlag.count,
                };
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(RefreshNotificationFlags.pending, (state) =>
                setPendingState(state)
            )
            .addCase(RefreshNotificationFlags.fulfilled, (state, action) => {
                state.flags = action.payload.data;
                state.deletedFlags = [];
                setSucceededState(state);
            })
            .addCase(RefreshNotificationFlags.rejected, (state) =>
                setRejectedState(state)
            )
            .addCase(RefreshNotificationFlagsExceptPrefix.pending, (state) =>
                setPendingState(state)
            )
            .addCase(
                RefreshNotificationFlagsExceptPrefix.fulfilled,
                (state, action) => {
                    const prefixesToIgnore = action.meta.arg.prefixesToIgnore;
                    let flagsToKeep = state.flags.filter((f) =>
                        prefixesToIgnore.some((p) =>
                            f.eventCode
                                .toLowerCase()
                                .startsWith(p.toLowerCase())
                        )
                    );
                    let newFlags: NotificationFlag[] = action.payload.data;
                    flagsToKeep
                        .filter((f) =>
                            newFlags.every(
                                (nf: NotificationFlag) =>
                                    nf.eventCode.toLowerCase() !==
                                    f.eventCode.toLowerCase()
                            )
                        )
                        .forEach((f) => newFlags.push(f));
                    state.flags = newFlags;
                    state.deletedFlags = [];
                    setSucceededState(state);
                }
            )
            .addCase(RefreshNotificationFlagsExceptPrefix.rejected, (state) =>
                setRejectedState(state)
            );
    },
});

const setRejectedState = (state: any) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = state;
};

const setPendingState = (state: any) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: any) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

export const {
    addNotificationFlagToDelete,
    deleteNotificationFlags,
    updateNotificationFlagCountByCode,
    removeNotificationFlagLocally,
} = notificationFlagsSlice.actions;
export default notificationFlagsSlice.reducer;
